import { uiLocales } from '@umodeapporg/ui/locales';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from '@/locales/en/common.json';
import ptCommon from '@/locales/pt/common.json';

import { localStorageManager } from './LocalStorageManager';

export const SUPPORTED_LANGUAGES = ['pt', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const DEFAULT_LANGUAGE: SupportedLanguage = 'pt';

class I18nManager {
  private static instance: I18nManager;
  private initialized = false;

  private constructor() {}

  public static getInstance(): I18nManager {
    if (!I18nManager.instance) {
      I18nManager.instance = new I18nManager();
    }
    return I18nManager.instance;
  }

  public initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    i18n.use(initReactI18next).init({
      lng: DEFAULT_LANGUAGE,
      fallbackLng: DEFAULT_LANGUAGE,
      resources: {
        pt: { common: ptCommon, ui: uiLocales.pt },
        en: { common: enCommon, ui: uiLocales.en },
      },
      defaultNS: 'common',
      interpolation: {
        escapeValue: false,
      },
    });

    // The sidebar group headings are library text. This product renames them by merging over
    // the bundle the library shipped, so the wording still lives in the locale files and not
    // in a component. The three names are the three groups the library can head (see
    // `navigationGroups`); the setup screens sit in the footer, which heads nothing.
    i18n.addResourceBundle(
      'pt',
      'ui',
      {
        sidebar: {
          sections: {
            favorites: ptCommon.nav.grupoDiaADia,
            management: ptCommon.nav.grupoPlanejamento,
            registration: ptCommon.nav.grupoGestao,
          },
        },
      },
      true,
      true,
    );
    i18n.addResourceBundle(
      'en',
      'ui',
      {
        sidebar: {
          sections: {
            favorites: enCommon.nav.grupoDiaADia,
            management: enCommon.nav.grupoPlanejamento,
            registration: enCommon.nav.grupoGestao,
          },
        },
      },
      true,
      true,
    );

    logger.info(`[I18nManager] Initialized with language: ${DEFAULT_LANGUAGE}`);
  }

  public syncStoredLanguage(): void {
    const saved = localStorageManager.getItem<SupportedLanguage>('language');
    if (saved !== null && SUPPORTED_LANGUAGES.includes(saved) && saved !== this.getLanguage()) {
      this.setLanguage(saved);
    }
  }

  public getLanguage(): SupportedLanguage {
    return (i18n.language as SupportedLanguage) ?? DEFAULT_LANGUAGE;
  }

  public setLanguage(lang: SupportedLanguage): void {
    i18n.changeLanguage(lang);
    localStorageManager.setItem('language', lang);
    logger.info(`[I18nManager] Language changed to: ${lang}`);
  }
}

export const i18nManager = I18nManager.getInstance();
